import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import productActions from "../../../../store/actions/items/item.actions";
import { useEffect, useState } from "react";
import TextInputElementComponent from "../../../general/elements/inputElements/TextInputElementComponent";
import textInputModel from "../../../../models/general/elements/inputElements/textInput.model";
import { SkeletonForm } from "../../../general/skeleton";
import useDelayedFlag from "../../../../hooks/useDelayedFlag";
import SwitchElementComponent from "../../../general/elements/inputElements/SwitchElementComponent";
import TextAreaInputElementComponent from "../../../general/elements/inputElements/TextAreaInputElementComponent";
import validationHelper from "../../../../helpers/validation.helper";
import AlertService from "../../../../services/alerts/alert.service";

export const UpdateItemExtensionsDialogComponent = ({ productActions }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // DialogManagerComponent is mounted in App.js, outside the route tree, so useParams() is empty
  // here — the admin id has to come from the path the dialog was opened over.
  const { pathname } = useLocation();
  const adminId = pathname.match(/\/admin\/dashboard\/([^/]+)/)?.[1];
  const [item, setItem] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const itemId = searchParams.get("itemId");
  const showSkeleton = useDelayedFlag(!item || loading);

  useEffect(() => {
    if (itemId) {
      fetchItemData();
    }
  }, [itemId]);

  const fetchItemData = async () => {
    setLoading(true);
    try {
      const response = await productActions.getProductById(itemId);
      if (response) {
        setItem(response);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setItem({ ...item, [name]: value });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setItem({ ...item, [name]: checked });
  };

  const validateValues = () => {
    const validationErrors = {};
    if (+item.minOrderQuantity <= 0) {
      validationErrors.minOrderQuantity = "כמות מינימום לא יכולה להיות שלילית.";
    }
    if (item.fullDescription && item.fullDescription.length > 500) {
      validationErrors.fullDescription = "תיאור הפריט יכול להכיל עד 500 תווים בלבד.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateValues()) return;
    try {
      // 0034: displayOrder is intentionally absent. The sequence has one writer — the display
      // order screen — so that two surfaces cannot hand out colliding positions.
      const updateData = {
        isAvailable: item.isAvailable,
        minOrderQuantity: item.minOrderQuantity,
        fullDescription: item.fullDescription,
        isPriceChanged: item.isPriceChanged,
      };
      const response = await productActions.updateItemExtension(item.itemId, updateData);
      if (!validationHelper.validateHttpResponse(response)) {
        setErrors({ general: "אירעה שגיאה בעדכון הפריט. נסה שוב מאוחר יותר." });
        return;
      }
      AlertService.success(`פריט ${item.itemId} - ${item.itemDescription} עודכן בהצלחה`);
    } catch (error) {
      setErrors({ general: "אירעה שגיאה בעדכון הפריט. נסה שוב מאוחר יותר." });
    }
  };

  const getIsAvailableSwitchElement = () => {
    const switchProps = {
      name: "isAvailable",
      label: "זמינות המוצר באתר",
      checked: item?.isAvailable || false,
      onChange: handleSwitchChange,
      customDesignClass: "customSwitchClass",
    };
    return <SwitchElementComponent props={switchProps} />;
  };

  const getIsPriceChangedSwitchElement = () => {
    const switchProps = {
      name: "isPriceChanged",
      label: "מחיר הפריט השנתנה",
      checked: item?.isPriceChanged || false,
      onChange: handleSwitchChange,
      customDesignClass: "customSwitchClass",
    };
    return <SwitchElementComponent props={switchProps} />;
  };

  // 0034: read-only. Editing the position here would fight the display order screen, which owns
  // the whole sequence and renumbers it as one arrangement.
  const getDisplayOrderElement = () => {
    return (
      <div className="readOnlyField">
        <label>סדר תצוגה</label>
        <div className="readOnlyValue">
          <span>{item.displayOrder}</span>
          {adminId && <Link to={`/admin/dashboard/${adminId}/items/order`}>שנה במסך סידור התצוגה</Link>}
        </div>
      </div>
    );
  };

  const getMinOrderQuantityInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "minOrderQuantity",
      error: errors.minOrderQuantity,
      value: item.minOrderQuantity,
      onChange: handleInputChange,
      label: "מינמום יחידות להזמנה (להגדרה כמארז)",
      type: "number",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getFullDescriptionInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "fullDescription",
      error: errors.fullDescription,
      value: item.fullDescription,
      onChange: handleInputChange,
      label: "תיאור פריט",
    };
    return <TextAreaInputElementComponent props={inputProps} />;
  };

  if (!item || loading) {
    return <div className="dialogContainer">{showSkeleton && <SkeletonForm fields={5} />}</div>;
  }

  return (
    <div className="dialogContainer">
      <h1>
        {item.itemId} - {item.itemDescription}
      </h1>
      <form onSubmit={handleSubmit} className="flex fdColumn gap10">
        {getIsAvailableSwitchElement()}
        {getIsPriceChangedSwitchElement()}
        {getDisplayOrderElement()}
        {getMinOrderQuantityInputElement()}
        {getFullDescriptionInputElement()}
        <button type="submit" className="primaryButton">
          עדכן
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state.categoryReducer.categories?.items, // Subscribe to categories state from the reducer
});

const mapDispatchToProps = (dispatch) => {
  return {
    productActions: bindActionCreators(productActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateItemExtensionsDialogComponent);
